import { useCallback, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { RollBlank } from "../models/RollBlank";
import { ROLL_BLANK_INITIAL_VALUES } from "../models/RollBlank";
import {
	useRollsBlanks,
	useMillSizes,
	useRollApplications,
	useRollMaterials,
} from "../hooks/useRollsBlanks";
import {
	useDeleteRollBlank,
	useUpsertRollBlank,
} from "../hooks/useRollBlankMutation";
import { RollsBlankTable } from "./RollsBlankTable";
import { RollBlankDetailViewer } from "./RollBlankDetailViewer";
import { RollsBlankToolbar } from "./RollsBlankToolbar";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import {
	urlParam,
	useDropUnknownValue,
	useUrlState,
	useUrlText,
} from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

const DEFAULT_LAYOUT = { left: 55, right: 45 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	millSizeId: urlParam.int(),
	rollApplicationId: urlParam.int(),
	rollMaterialId: urlParam.int(),
	rollBlankId: urlParam.int(),
};

export const RollsBlankManager = () => {
	// Filters and the selected record, all in the query string.
	const [
		{
			search,
			onlyActives,
			millSizeId,
			rollApplicationId,
			rollMaterialId,
			rollBlankId,
		},
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	// A link to a roll material that is no longer offered falls back to all of them.
	const {
		data: rollMaterialOptions = [],
		isSuccess: rollMaterialOptionsLoaded,
	} = useRollMaterials();
	useDropUnknownValue(
		rollMaterialId,
		rollMaterialOptionsLoaded,
		rollMaterialOptions.map((option) => option.Id),
		useCallback(() => dropValues("rollMaterialId"), [dropValues]),
	);
	// A link to a roll application that is no longer offered falls back to all of them.
	const {
		data: rollApplicationOptions = [],
		isSuccess: rollApplicationOptionsLoaded,
	} = useRollApplications();
	useDropUnknownValue(
		rollApplicationId,
		rollApplicationOptionsLoaded,
		rollApplicationOptions.map((option) => option.Id),
		useCallback(() => dropValues("rollApplicationId"), [dropValues]),
	);
	// A link to a mill size that is no longer offered falls back to all of them.
	const { data: millSizeOptions = [], isSuccess: millSizeOptionsLoaded } =
		useMillSizes();
	useDropUnknownValue(
		millSizeId,
		millSizeOptionsLoaded,
		millSizeOptions.map((option) => option.Id),
		useCallback(() => dropValues("millSizeId"), [dropValues]),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [blankToDelete, setBlankToDelete] = useState<RollBlank | null>(null);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "rolls-blank-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const { rollBlanks, isLoading, error: queryError } = useRollsBlanks();

	const filteredBlanks = useMemo(() => {
		let result = onlyActives ? rollBlanks.filter((b) => b.Active) : rollBlanks;
		if (searchText) {
			const s = searchText.toLowerCase();
			result = result.filter(
				(b) =>
					b.Dwg?.toLowerCase().includes(s) ||
					b.Description?.toLowerCase().includes(s),
			);
		}
		if (millSizeId) {
			result = result.filter((b) => b.MillSizeId === millSizeId);
		}
		if (rollApplicationId) {
			result = result.filter((b) => b.RollApplicationId === rollApplicationId);
		}
		if (rollMaterialId) {
			result = result.filter((b) => b.RollMaterialId === rollMaterialId);
		}
		return result;
	}, [
		rollBlanks,
		searchText,
		millSizeId,
		rollApplicationId,
		rollMaterialId,
		onlyActives,
	]);

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<RollBlank>({
		id: rollBlankId,
		setId: (value) => setFilters({ rollBlankId: value }),
		dropId: () => dropValues("rollBlankId"),
		rows: rollBlanks,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedBlank = selection.current;

	const { upsertRollBlank, isUpserting } = useUpsertRollBlank({
		onSuccess: (data) => {
			const isNew = selection.isNew;
			setNotification(
				isNew
					? "Roll blank created successfully"
					: "Roll blank updated successfully",
			);
			if (data) {
				selection.adopt(data);
			}
		},
		onError: (err) => setError(err.message),
	});

	const { deleteRollBlank, isDeleting } = useDeleteRollBlank({
		onSuccess: () => {
			setNotification("Roll blank deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setBlankToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	const handleRowSelected = useCallback(
		(blank: RollBlank | null) => {
			if (blank) selection.select(blank);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((blank: RollBlank) => {
		setBlankToDelete(blank);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (blankToDelete?.Id) {
			deleteRollBlank(blankToDelete.Id);
		}
	}, [blankToDelete, deleteRollBlank]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setBlankToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		selection.startNew({ ...ROLL_BLANK_INITIAL_VALUES });
	}, [selection]);

	const handleSave = useCallback(
		(blank: RollBlank) => {
			upsertRollBlank(blank);
		},
		[upsertRollBlank],
	);

	const handleClose = useCallback(() => {
		selection.clear();
	}, [selection]);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Rolls Blank
			</Typography>

			<Paper elevation={3}>
				<RollsBlankToolbar
					onReset={() =>
						resetValues([
							"search",
							"onlyActives",
							"millSizeId",
							"rollApplicationId",
							"rollMaterialId",
						])
					}
					canReset={
						!isDefault(
							"search",
							"onlyActives",
							"millSizeId",
							"rollApplicationId",
							"rollMaterialId",
						)
					}
					search={searchText}
					onSearchChange={setSearchText}
					millSizeId={millSizeId}
					onMillSizeChange={(value) => setFilters({ millSizeId: value })}
					rollApplicationId={rollApplicationId}
					onRollApplicationChange={(value) =>
						setFilters({ rollApplicationId: value })
					}
					rollMaterialId={rollMaterialId}
					onRollMaterialChange={(value) =>
						setFilters({ rollMaterialId: value })
					}
					onlyActives={onlyActives}
					onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
					onAdd={handleAdd}
				/>
			</Paper>

			<Group
				id="rolls-blank-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={3}
							sx={{ flex: 1, width: "100%", overflow: "hidden" }}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<RollsBlankTable
									rollBlanks={filteredBlanks}
									selectedBlank={selectedBlank?.Id ? selectedBlank : null}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								overflowY: "auto",
								overflowX: "hidden",
							}}
						>
							<RollBlankDetailViewer
								blank={selectedBlank}
								existingBlanks={rollBlanks}
								isSaving={isUpserting || isDeleting}
								onSave={handleSave}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE ROLL BLANK"
				itemName={blankToDelete?.Dwg ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
